import { Injectable } from '@angular/core';
import { NavController } from '@ionic/angular';
import { CartResolver } from 'src/app/appResolvers/cart.resolver';
import { MenuResolver } from 'src/app/appResolvers/menu.resolver';
import { RestaurantInfoResolver } from 'src/app/appResolvers/restaurant-info.resolver';
import { StoreResolver } from 'src/app/appResolvers/store.resolver';
import { TokenService } from 'src/app/core/auth/token.service';
import { CdsResolver } from 'src/app/pages/cds/cds.resolver';
import { TransactionResolver } from 'src/app/pages/transactions/transaction.resolver';
import { ClientTypeResolver } from '../client-type/client-type.resolver';
import { GlobalAuthResolver } from '../global-auth/global-auth.resolver';
import { TenantResolver } from '../tenant/tenant.resolver';
import { StorageService } from '../../core/services/storage.service';

@Injectable({
  providedIn: 'root',
})
export class LogoutResolver {
  private static readonly TRANSITION_PRIORITY = {
    expire: 0,
    lock: 1,
    shiftEnd: 2,
    changeDomain: 3,
    logout: 4,
  } as const;

  private sessionEndQueue: Promise<void> = Promise.resolve();
  private readonly sessionTransitionsInFlight = new Map<
    string,
    Promise<void>
  >();
  private dominantSessionTransition: {
    priority: number;
    promise: Promise<void>;
  } | null = null;

  constructor(
    private restaurantInfoResolver: RestaurantInfoResolver,
    private tenantResolver: TenantResolver,
    private tokenService: TokenService,
    private menuResolver: MenuResolver,
    private cartResolver: CartResolver,
    private navCtrl: NavController,
    private globalAuthResolver: GlobalAuthResolver,
    private storeResolver: StoreResolver,
    private transactionResolver: TransactionResolver,
    private clientTypeResolver: ClientTypeResolver,
    private cdsResolver: CdsResolver,
    private storageService: StorageService,
  ) {}

  async clearMenuInStorage(): Promise<void> {
    const keys = await this.storageService.keys();
    const keysToRemove = keys.filter((key) =>
      key.startsWith('productList_'),
    );
    for (const key of keysToRemove) {
      await this.storageService.remove(key);
    }
  }

  /**
   * End a rejected/expired session. TokenService has already invalidated the
   * exact bearer that failed, so this path only tears down authenticated UI.
   * It deliberately leaves the cart and tenant untouched for the same
   * restaurant to resume after login.
   */
  expireSession(): Promise<void> {
    return this.endAuthenticatedSession(
      '/auth/login',
      false,
      LogoutResolver.TRANSITION_PRIORITY.expire,
    );
  }

  /** Lock the POS without ending the shift or clearing its current cart. */
  lockSession(): Promise<void> {
    return this.endAuthenticatedSession(
      '/auth/login',
      true,
      LogoutResolver.TRANSITION_PRIORITY.lock,
    );
  }

  /** Preserve the stage_v2 explicit logout behaviour. */
  logout(): Promise<void> {
    return this.enqueueSessionEnd('full-logout', async () => {
      await this.cartResolver.clearCart();
      this.resetRuntimeSession();
      await this.tokenService.clearToken();
      await this.clearMenuInStorage();
      await this.tenantResolver.clearTenantName();
      await this.restaurantInfoResolver.clearBasicInfo();
      await this.storageService.remove('store');
      await this.storageService.remove('user');
      await this.navCtrl.navigateRoot('/auth');
    }, LogoutResolver.TRANSITION_PRIORITY.logout);
  }

  /** Preserve the stage_v2 shift-end behaviour. */
  shiftEnd(): Promise<void> {
    return this.enqueueSessionEnd('shift-end', async () => {
      await this.cartResolver.clearCart();
      this.resetRuntimeSession();
      await this.tokenService.clearToken();
      await this.storageService.remove('user');
      await this.navCtrl.navigateRoot('/auth/login');
    }, LogoutResolver.TRANSITION_PRIORITY.shiftEnd);
  }

  /**
   * Leave the PIN screen for another restaurant without routing through the
   * full logout path. This centralizes the cleanup formerly split
   * between the login and domain pages.
   */
  changeDomain(): Promise<void> {
    return this.enqueueSessionEnd('domain-change', async () => {
      this.resetRuntimeSession();
      await this.tokenService.clearToken();
      await this.clearMenuInStorage();
      await this.tenantResolver.clearTenantName();
      await this.restaurantInfoResolver.clearBasicInfo();
      await this.storageService.remove('store');
      await this.storageService.remove('user');
      await this.navCtrl.navigateRoot('/auth/domain');
    }, LogoutResolver.TRANSITION_PRIORITY.changeDomain);
  }

  private endAuthenticatedSession(
    target: string,
    clearToken: boolean,
    priority: number,
  ): Promise<void> {
    return this.enqueueSessionEnd(
      `session:${target}:${clearToken ? 'clear' : 'keep'}`,
      async () => {
        this.resetRuntimeSession();
        if (clearToken) {
          try {
            await this.tokenService.clearToken();
          } catch (error) {
            // Memory is already fail-closed. A native Preferences failure must
            // not keep an expired user inside authenticated screens.
            console.error('Unable to persist token cleanup', error);
          }
        }
        try {
          await this.storageService.remove('user');
        } catch (error) {
          console.error('Unable to clear cached user profile', error);
        }
        await this.navCtrl.navigateRoot(target);
      },
      priority,
    );
  }

  private resetRuntimeSession(): void {
    this.globalAuthResolver.endSession();
    this.cdsResolver.destroySubscriptions();
    this.menuResolver.clearMenu(true);
    this.storeResolver.clear();
    this.transactionResolver.clear();
    this.clientTypeResolver.clear();
  }

  private enqueueSessionEnd(
    key: string,
    work: () => Promise<void>,
    priority: number,
  ): Promise<void> {
    const existing = this.sessionTransitionsInFlight.get(key);
    if (existing) {
      return existing;
    }

    // A lower-priority transition must never overwrite the final destination
    // of an explicit operation already in progress. In particular, a late
    // expiry event cannot turn a completed full logout (`/auth`) into the PIN
    // login route after the tenant has already been removed. If that dominant
    // operation rejects, however, the lower-priority fail-closed transition
    // must still run: clearCart can intentionally reject to protect unresolved
    // payment evidence while an independently invalidated token still requires
    // the authenticated UI to be torn down.
    if (
      this.dominantSessionTransition &&
      this.dominantSessionTransition.priority > priority
    ) {
      const dominantPromise = this.dominantSessionTransition.promise;
      let fallback: Promise<void>;
      fallback = dominantPromise
        .catch(() => {
          if (this.sessionTransitionsInFlight.get(key) === fallback) {
            this.sessionTransitionsInFlight.delete(key);
          }
          if (this.dominantSessionTransition?.promise === dominantPromise) {
            this.dominantSessionTransition = null;
          }
          return this.enqueueSessionEnd(key, work, priority);
        })
        .finally(() => {
          if (this.sessionTransitionsInFlight.get(key) === fallback) {
            this.sessionTransitionsInFlight.delete(key);
          }
        });
      this.sessionTransitionsInFlight.set(key, fallback);
      return fallback;
    }

    const result = this.sessionEndQueue
      .then(work, work)
      .finally(() => {
        if (this.sessionTransitionsInFlight.get(key) === result) {
          this.sessionTransitionsInFlight.delete(key);
        }
      });
    this.sessionTransitionsInFlight.set(key, result);
    if (
      !this.dominantSessionTransition ||
      priority >= this.dominantSessionTransition.priority
    ) {
      this.dominantSessionTransition = { priority, promise: result };
    }
    this.sessionEndQueue = result.catch(() => undefined);
    void result.finally(() => {
      if (this.dominantSessionTransition?.promise === result) {
        this.dominantSessionTransition = null;
      }
    }).catch(() => undefined);
    return result;
  }
}
